import { combineEpics } from 'redux-observable';
import { filter, map, mapTo, merge, mergeMap, of } from 'rxjs';
import { isOfType } from 'typesafe-actions';
import { catchAxiosError, catchUnauthorizedError, createEpic$ } from 'lib/store';
import { notificationError, notificationSuccess, notificationWarning } from 'store/notifications';
import { systemUnauthorized } from 'store/system';
import { bankTransactionSelect, systemTransactionSelect } from 'store/transactions';
import {
  TransactionHandleAction,
  transactionHandleError,
  transactionMatchResponse,
  transactionMatchResult,
  transactionUnmatchResponse,
  transactionAutoMatchResponse,
  transactionAutoMatchResult
} from './actions';
import bankHandleEpic$ from './bank/epics';
import systemHandleEpic$ from './system/epics';

const transactionsClearSelectionEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(TransactionHandleAction.MatchResponse)),
    mergeMap(() => merge(of(bankTransactionSelect([])), of(systemTransactionSelect([])))),
  ),
);

const transactionsErrorEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(TransactionHandleAction.Error)),
    mapTo(
      notificationError({
        caption: 'Error',
        duration: 10000,
        message: 'There was a problem processing transactions',
      }),
    ),
  ),
);

const transactionsMatchEpic$ = createEpic$(({ action$, matchTransaction$ }) =>
  action$.pipe(
    filter(isOfType(TransactionHandleAction.MatchRequest)),
    mergeMap(action =>
      matchTransaction$(action.payload).pipe(
        mergeMap(response =>
          merge(
            of(transactionMatchResponse(response)),
            of(transactionMatchResult({ ...action.payload, ...response })),
          ),
        ),
        catchAxiosError(transactionHandleError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const transactionsAutoMatchEpic$ = createEpic$(({ action$, autoMatchTransaction$ }) =>
    action$.pipe(
        filter(isOfType(TransactionHandleAction.AutoMatchRequest)),
        mergeMap(action =>
            autoMatchTransaction$(action.payload).pipe(
                mergeMap(response =>
                    merge(
                        of(transactionAutoMatchResponse(response)),
                        of(transactionAutoMatchResult({ ...action.payload, ...response })),
                    ),
                ),
                catchAxiosError(transactionHandleError),
                catchUnauthorizedError(systemUnauthorized),
            ),
        ),
    ),
);

const transactionsMatchSuccessEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(TransactionHandleAction.MatchResponse)),
    mapTo(
      notificationSuccess({
        caption: 'Success',
        message: `Transactions successfully matched`,
      }),
    ),
  ),
);

const transactionsUnmatchEpic$ = createEpic$(({ action$, unmatchTransaction$ }) =>
  action$.pipe(
    filter(isOfType(TransactionHandleAction.UnmatchRequest)),
    mergeMap(action =>
      unmatchTransaction$({
        mappedTransactionMappingIds: action.payload,
      }).pipe(
        map(() => transactionUnmatchResponse(action.payload)),
        catchAxiosError(transactionHandleError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const transactionsUnmatchSuccessEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(TransactionHandleAction.UnmatchResponse)),
    mapTo(
      notificationWarning({
        caption: 'Success',
        message: `Transactions successfully unmatched`,
      }),
    ),
  ),
);

// const transactionsAutoMatchSuccessEpic$ = createEpic$(({ action$ }) =>
//   action$.pipe(
//     filter(isOfType(TransactionHandleAction.AutoMatchResponse)),
//     mapTo(
//       notificationSuccess({
//         caption: 'Success',
//         message: `Transactions successfully automatched`,
//       }),
//     ),
//   ),
// );
const transactionsAutoMatchSuccessEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(TransactionHandleAction.AutoMatchResponse)),
    map(action => {
      const autoMatchedCount = action.payload?.autoMatchedCount ?? 0;
      const totalAutoMatchedAmount = action.payload?.totalAutoMatchedAmount ?? 0;
      return notificationSuccess({
        caption: 'Success',
        message: `${autoMatchedCount} transaction(s) successfully auto-matched. Total Amount ${totalAutoMatchedAmount} `,
      });
    }),
  ),
);

export default combineEpics(
  bankHandleEpic$,
  systemHandleEpic$,
  transactionsClearSelectionEpic$,
  transactionsErrorEpic$,
  transactionsMatchEpic$,
  transactionsMatchSuccessEpic$,
  transactionsUnmatchEpic$,
  transactionsUnmatchSuccessEpic$,
  transactionsAutoMatchEpic$,
  transactionsAutoMatchSuccessEpic$
);
